import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import { ContentStateHandler, ErrorDisplay } from '../../components/common';
import { getApiDefinitionOpenApiSpec } from '../../services/api-repositories/api-definitions-repository';

interface SpecsTabState {
  openApiSpec: string | object | undefined;
  loading: boolean;
  error: string | null;
}

const SpecsTab: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const [state, setState] = useState<SpecsTabState>({
    openApiSpec: undefined,
    loading: true,
    error: null,
  });

  const { openApiSpec, loading, error } = state;

  useEffect(() => {
    const fetchOpenApiSpec = async () => {
      if (!slug) {
        setState((prev: SpecsTabState) => ({
          ...prev,
          error: t('apiDetail.specsTab.apiIdRequired'),
          loading: false,
        }));
        return;
      }

      try {
        setState((prev: SpecsTabState) => ({
          ...prev,
          loading: true,
          error: null,
        }));
        const spec = await getApiDefinitionOpenApiSpec(slug);

        setState((prev: SpecsTabState) => ({
          ...prev,
          openApiSpec: spec,
          loading: false,
        }));
      } catch (err: unknown) {
        console.error('Failed to fetch OpenAPI specification:', err);
        const errorMessage =
          err instanceof Error ? err.message : t('apiDetail.specsTab.failedToLoad');
        setState((prev: SpecsTabState) => ({
          ...prev,
          error: errorMessage,
          loading: false,
        }));
      }
    };

    fetchOpenApiSpec();
  }, [slug, t]);

  if (!loading && !error && !openApiSpec) {
    return (
      <ErrorDisplay
        error={t('apiDetail.specsTab.noSpecAvailable')}
        severity='warning'
        description={t('apiDetail.specsTab.noSpecMessage')}
        padding={3}
      />
    );
  }

  return (
    <ContentStateHandler
      loading={loading}
      error={error}
      hasContent={!!openApiSpec}
      emptyMessage={t('apiDetail.specsTab.noSpecAvailable')}
    >
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            '& .swagger-ui': {
              fontFamily: 'inherit',
            },
            '& .swagger-ui .topbar': {
              display: 'none',
            },
            '& .swagger-ui .info': {
              marginTop: 0,
            },
            '& .swagger-ui .scheme-container': {
              background: 'transparent',
              boxShadow: 'none',
            },
            '& .swagger-ui .opblock-tag': {
              fontSize: '1.1rem',
              fontWeight: 600,
            },
          }}
        >
          <SwaggerUI
            spec={openApiSpec}
            displayRequestDuration={true}
            docExpansion='list'
            filter={true}
            showExtensions={true}
            showCommonExtensions={false}
            tryItOutEnabled={true}
            supportedSubmitMethods={['get', 'post', 'put', 'delete', 'patch']}
          />
        </Box>
      </Box>
    </ContentStateHandler>
  );
};

export default SpecsTab;
